import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Database, 
  Package, 
  Plus, 
  Library, 
  BarChart3, 
  Users, 
  Target,
  FileText,
  Settings,
  ExternalLink
} from 'lucide-react';
import { ModuleAccessGate } from '@/components/shared/ModuleAccessGate';
import { MaterialityOverview } from './MaterialityOverview';
import { MaterialityMatrix } from './MaterialityMatrix';
import { StakeholderMapping } from './StakeholderMapping';
import { IndustryBenchmarks } from './IndustryBenchmarks';
import { MaterialityReports } from './MaterialityReports';
import { MaterialitySettings } from './MaterialitySettings';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { MODULE_ACCESS } from '@/constants/moduleAccess';

export const MaterialityAssessmentMain: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleNavigateToLibrary = () => {
    navigate('/data-management');
    toast({
      title: 'Navigating to Library',
      description: 'Redirecting to the unified Data Management library',
    });
  };

  return (
    <ModuleAccessGate moduleName={MODULE_ACCESS.DOUBLE_MATERIALITY_ASSESSMENT}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Materiality Assessment</h1>
            <p className="text-muted-foreground">
              Comprehensive double materiality assessment for ESG reporting and sustainability strategy
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              Individual+ Feature
            </Badge>
          </div>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="library" className="flex items-center gap-2">
              <Library className="h-4 w-4" />
              Library
            </TabsTrigger>
            <TabsTrigger value="matrix" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Matrix
            </TabsTrigger>
            <TabsTrigger value="stakeholders" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Stakeholders
            </TabsTrigger>
            <TabsTrigger value="benchmarks" className="flex items-center gap-2">
              <Database className="h-4 w-4" />
              Benchmarks
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <MaterialityOverview />
          </TabsContent>

          {/* Library Tab */}
          <TabsContent value="library" className="space-y-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold">Data Library</h2>
                  <p className="text-muted-foreground">
                    Access the unified library system for metrics and materials data
                  </p>
                </div>
              </div>
              
              {/* Library Access Cards */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Metrics Library Card */}
                <Card className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Database className="h-5 w-5" />
                      Metrics Library
                    </CardTitle>
                    <CardDescription>
                      Comprehensive sustainability metrics with standards mapping
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Categories:</span>
                        <Badge variant="outline">Environmental, Social, Governance, Economic, Material</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Standards:</span>
                        <Badge variant="outline">GRI, SASB, TCFD, CDP, IFRS</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Features:</span>
                        <Badge variant="outline">Search, Filter, Export, Customization</Badge>
                      </div>
                    </div>
                    <Button 
                      onClick={handleNavigateToLibrary}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Access Metrics Library
                    </Button>
                  </CardContent>
                </Card>

                {/* Materials Library Card */}
                <Card className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Materials Library
                    </CardTitle>
                    <CardDescription>
                      Materials database with sustainability metrics and emission factors
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Categories:</span>
                        <Badge variant="outline">Metals, Plastics, Wood & Paper, Textiles, Electronics</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Metrics:</span>
                        <Badge variant="outline">Carbon Footprint, Density, Cost, Sustainability Rating</Badge>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span>Features:</span>
                        <Badge variant="outline">Search, Filter, Customization, Integration</Badge>
                      </div>
                    </div>
                    <Button 
                      onClick={handleNavigateToLibrary}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Access Materials Library
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Information Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Unified Library System</CardTitle>
                  <CardDescription>
                    All library functionality is now centralized in the Data Management module
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Centralized Access</h4>
                      <p className="text-sm text-blue-800">
                        All metrics and materials are now managed in one unified library system accessible from the Data Management module.
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">Cross-Module Integration</h4>
                      <p className="text-sm text-green-800">
                        Data selected from the library is automatically available across all modules including Materiality Assessment.
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h4 className="font-medium text-purple-900 mb-2">User Data Management</h4>
                      <p className="text-sm text-purple-800">
                        Customize library data for your organization and save it to your storage location for use in assessments.
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-medium text-yellow-900 mb-2">How to Use</h4>
                    <ol className="text-sm text-yellow-800 space-y-1 list-decimal list-inside">
                      <li>Navigate to the Data Management module</li>
                      <li>Browse the Metrics Library or Materials Library</li>
                      <li>Search and filter to find relevant data</li>
                      <li>Select and customize data for your organization</li>
                      <li>Use the data in your Materiality Assessment</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Matrix Tab */}
          <TabsContent value="matrix" className="space-y-6">
            <MaterialityMatrix />
          </TabsContent>

          {/* Stakeholders Tab */}
          <TabsContent value="stakeholders" className="space-y-6">
            <StakeholderMapping />
          </TabsContent>

          {/* Benchmarks Tab */}
          <TabsContent value="benchmarks" className="space-y-6">
            <IndustryBenchmarks />
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <MaterialityReports />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <MaterialitySettings />
          </TabsContent>
        </Tabs>
      </div>
    </ModuleAccessGate>
  );
}; 
